import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowRight } from "@phosphor-icons/react";

const STATUS_TONE = {
  active: "bg-[#A8977B]/20 text-[#5a4d34]",
  paused: "bg-[#efece3] text-[#606C64]",
  completed: "bg-[#1A2F24] text-[#F7F6F2]",
};

const EngagementsPage = () => {
  const [data, setData] = useState({ items: [], workflow_steps: [] });
  const [clients, setClients] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    client_id: "",
    client_name: "",
    client_email: "",
    service_type: "Tax Prep",
    fee: 5000,
    description: "",
  });

  const load = async () => {
    const [r, c] = await Promise.all([api.get("/engagements"), api.get("/clients")]);
    setData(r.data);
    setClients(c.data.items);
  };

  useEffect(() => {
    load();
  }, []);

  const pickClient = (cid) => {
    const c = clients.find((x) => x.id === cid);
    if (c) {
      setForm({
        ...form,
        client_id: c.id,
        client_name: c.name,
        client_email: c.email || form.client_email,
      });
    } else {
      setForm({ ...form, client_id: "" });
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, fee: Number(form.fee) };
      if (!payload.client_id) delete payload.client_id;
      await api.post("/engagements", payload);
      toast.success("Engagement created.");
      setShowNew(false);
      setForm({
        client_id: "",
        client_name: "",
        client_email: "",
        service_type: "Tax Prep",
        fee: 5000,
        description: "",
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create engagement");
    }
  };

  return (
    <div data-testid="engagements-page" className="space-y-8">
      <div className="flex items-end justify-between pb-8 border-b border-[#1A2F24]/12">
        <div>
          <p className="label-eyebrow">Workflow</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#112018] mt-2">Engagements</h1>
          <p className="text-[#606C64] mt-2 text-sm max-w-xl">
            Every engagement follows a seven-step path — drafted, sent, signed, documented,
            performed, reviewed, closed.
          </p>
        </div>
        <button
          data-testid="new-engagement-btn"
          onClick={() => setShowNew(!showNew)}
          className="btn-primary"
        >
          <Plus size={14} className="inline mr-2" />
          New Engagement
        </button>
      </div>

      {showNew && (
        <form
          onSubmit={create}
          className="card-flat space-y-4 fade-up"
          data-testid="new-engagement-form"
        >
          <h2 className="font-serif text-2xl text-[#112018]">Create engagement</h2>

          <div>
            <label className="label-eyebrow block mb-2">Existing client (optional)</label>
            <select
              data-testid="eng-existing-client"
              className="input-clean"
              value={form.client_id}
              onChange={(e) => pickClient(e.target.value)}
            >
              <option value="">— New client (fill below) —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.email ? `· ${c.email}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">Client name</label>
              <input
                data-testid="eng-client-name"
                required
                disabled={!!form.client_id}
                className="input-clean"
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Client email</label>
              <input
                data-testid="eng-client-email"
                type="email"
                required
                disabled={!!form.client_id}
                className="input-clean"
                value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })}
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Service</label>
              <select
                data-testid="eng-service-type"
                className="input-clean"
                value={form.service_type}
                onChange={(e) => setForm({ ...form, service_type: e.target.value })}
              >
                <option>Tax Prep</option>
                <option>Audit</option>
                <option>Advisory</option>
                <option>Bookkeeping</option>
                <option>Forensic</option>
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Fee (USD)</label>
              <input
                data-testid="eng-fee"
                type="number"
                min="0"
                step="100"
                className="input-clean"
                value={form.fee}
                onChange={(e) => setForm({ ...form, fee: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Description / scope</label>
            <textarea
              data-testid="eng-description"
              rows={3}
              className="input-clean"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Scope, timeline, deliverables…"
            />
          </div>
          <div className="flex gap-3">
            <button type="submit" data-testid="eng-submit" className="btn-primary">
              Create Engagement
            </button>
            <button
              type="button"
              onClick={() => setShowNew(false)}
              className="btn-outline"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Engagement list */}
      {data.items.length === 0 ? (
        <div className="card-flat text-center py-16">
          <p className="font-serif text-3xl text-[#112018]">No engagements yet.</p>
          <p className="text-[#606C64] mt-2 text-sm">
            Press "New Engagement" to create your first one.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((e) => {
            const total = data.workflow_steps.length || 7;
            const pct = ((e.current_step ?? 0) / (total - 1)) * 100;
            const stepLabel =
              data.workflow_steps[e.current_step ?? 0]?.label || "—";
            return (
              <Link
                key={e.id}
                to={`/engagements/${e.id}`}
                data-testid={`engagement-row-${e.id}`}
                className="card-flat block hover:translate-y-[-2px] transition-transform"
              >
                <div className="grid md:grid-cols-[2fr_1fr_1fr_auto] gap-4 items-center">
                  <div>
                    <p className="font-serif text-2xl text-[#112018]">{e.client_name}</p>
                    <p className="label-eyebrow mt-1">{e.client_email}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Service</p>
                    <p className="text-[#112018] mt-1">{e.service_type}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Fee</p>
                    <p className="font-mono text-[#1A2F24] mt-1">
                      ${e.fee.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${
                        STATUS_TONE[e.status] || STATUS_TONE.active
                      }`}
                    >
                      {e.status}
                    </span>
                    <ArrowRight size={16} className="text-[#A8977B]" />
                  </div>
                </div>
                <div className="mt-5">
                  <div className="flex justify-between mb-2">
                    <span className="label-eyebrow">
                      Step {(e.current_step ?? 0) + 1} / {total} · {stepLabel}
                    </span>
                    <span className="label-eyebrow">{Math.round(pct)}%</span>
                  </div>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EngagementsPage;
